// utils/apiService.js - سرویس‌های API (Functional)
import { conf } from '../../../confg';

/**
 * درخواست عمومی API
 * @param {string} endpoint - نقطه پایانی API
 * @param {Object} options - تنظیمات درخواست
 * @returns {Promise<Object>} - پاسخ API
 */
const apiRequest = async (endpoint, options = {}) => {
  const defaultOptions = {
    headers: {
      'Content-Type': 'application/json',
      ...(localStorage.getItem('token') && {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      }),
      ...options.headers,
    },
  };

  try {
    const response = await fetch(conf.server_url + endpoint, {
      ...defaultOptions,
      ...options,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`API Error for ${endpoint}:`, error);
    throw error;
  }
};

/**
 * دریافت آدرس پیش‌فرض کاربر
 * @param {string} userId - شناسه کاربر
 * @returns {Promise<Object|null>} - آدرس پیش‌فرض یا null
 */
export const getDefaultAddress = async (userId) => {
  if (!userId) return null;

  const form = new FormData();
  form.append('customer_id', userId);

  try {
    const response = await fetch(conf.server_url + 'addresses/default', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
      body: form,
    });

    // 400/404 در این endpoint معمولا یعنی آدرس پیش‌فرض ثبت نشده
    if (response.status === 400 || response.status === 404) {
      return null;
    }

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    return data.address?.default_address || null;
  } catch (error) {
    // خطای شبکه/سرور: برای جلوگیری از خطای مزاحم در UI فقط null برگردان
    return null;
  }
};

/**
 * حذف محصول از سبد خرید سرور
 * @param {string} productId - شناسه محصول
 * @returns {Promise<boolean>} - موفقیت عملیات
 */
export const removeProductFromCart = async (productId) => {
  if (!productId) return false;

  try {
    const response = await apiRequest('cart/remove', {
      method: 'POST',
      body: JSON.stringify({ product_id: productId }),
    });
    return !!response;
  } catch (error) {
    console.error('Error removing product from cart:', error);
    return false;
  }
};

/**
 * دریافت اطلاعات سبد خرید
 * @returns {Promise<Object|null>} - اطلاعات سبد خرید
 */
export const getCartInfo = async () => {
  try {
    return await apiRequest('cart', { method: 'GET' });
  } catch (error) {
    console.error('Error fetching cart info:', error);
    return null;
  }
};

/**
 * بروزرسانی تعداد محصول در سبد
 * @param {string} productId - شناسه محصول
 * @param {number} quantity - تعداد جدید
 * @returns {Promise<boolean>} - موفقیت عملیات
 */
export const updateProductQuantity = async (productId, quantity) => {
  if (!productId || quantity < 0) return false;

  try {
    const response = await apiRequest('cart/update', {
      method: 'PUT',
      body: JSON.stringify({
        product_id: productId,
        quantity: quantity,
      }),
    });
    return !!response;
  } catch (error) {
    console.error('Error updating product quantity:', error);
    return false;
  }
};

/**
 * اعمال کد تخفیف
 * @param {string} couponCode - کد تخفیف
 * @returns {Promise<Object|null>} - نتیجه اعمال کد تخفیف
 */
export const applyCoupon = async (couponCode) => {
  if (!couponCode) return null;

  try {
    return await apiRequest('cart/coupon', {
      method: 'POST',
      body: JSON.stringify({ coupon_code: couponCode }),
    });
  } catch (error) {
    console.error('Error applying coupon:', error);
    return null;
  }
};

// Default export object
const ApiService = {
  getDefaultAddress,
  removeProductFromCart,
  getCartInfo,
  updateProductQuantity,
  applyCoupon,
};

export default ApiService;
